import { useEffect, useRef, useState } from 'react';

import { useLanguage } from '../../../shared/hooks/use-language';
import { BackendValidationError, parseBackendErrors } from '../../../shared/lib/backend-errors';
import {
  composeReferenceTimeUtcMidnight,
  formatDate,
  isValidCalendarDate,
} from '../../../shared/lib/date-utils';
import { isUnreadableResponseError } from '../../../shared/lib/response-shape';
import {
  ADOPTABLE_ATTACHMENT_STATUSES,
  csvRejection,
  CsvFileError,
  findAttachmentByUniqueId,
  readCsvText,
  uploadAttachment,
  uploadUniqueIdFromContent,
  type AttachmentStatus,
  type CsvRejectionCode,
} from '../api/attachment';
import {
  completenessNotAppliedMessage,
  createParseRequests,
  findParseRequestByUniqueId,
  IMPORT_TYPE_CONFIG,
  isTerminalParseStatus,
  parseRequestUniqueId,
  suggestedRequestTypeFromReasons,
  type ParseRequestType,
  type ParseRequestStatus,
  type ParseResult,
} from '../api/parse-request';
import {
  useAttachmentScanQuery,
  useParseRequestQuery,
  useParseResultQuery,
} from './use-parse-import-queries';
import type { ScopeContext } from '../../auth/model/types';

/**
 * Backend parameter path -> local field id. The upload's own rejections land on the
 * `file` part; everything the parse request can refuse (`attachment_id`,
 * `portfolio_id`) is decided by the page rather than typed by the user, so those flow
 * into the general banner where they read as what they are — a refusal of the request,
 * not a correction the user can make in a field. `reference_time` is user-typed, so it
 * is mapped like `file` — though by the time the dispatch that sends it runs, the form
 * carrying its field has already been replaced by the progress card (see
 * `referenceDate` below), so a backend rejection of it still has nowhere but the
 * general banner to land, same as `file`'s today.
 */
const IMPORT_FIELD_MAP: Record<string, string> = {
  file: 'file',
  reference_time: 'referenceTime',
};

/** `YYYY-MM-DD` for today, in the viewer's own local calendar day — the same reading
 *  `DateField` and every other local date field in the console use. */
const today = (): string => formatDate(new Date(), 'YYYY-MM-DD');

/**
 * Renders both a field-specific backend message and whatever else came back in the same
 * response into the one general banner this pipeline has, instead of the field message
 * winning outright and silently dropping every other error the backend named. Both
 * `submit` and the dispatch effect below land a field-specific rejection here because, by
 * the time either runs, the form field that error is *about* is already gone from the
 * screen — see each call site's own comment for why.
 */
const combineWithGeneralError = (fieldMessage: string | undefined, generalError: string | null): string | null =>
  [fieldMessage, generalError].filter((part): part is string => Boolean(part)).join('; ') || null;

export interface UseCsvImportOptions {
  portfolioId: string | undefined;
  sessionId: string | null;
  accountScope: ScopeContext | null;
  /** Called once per request whose settle booked rows, so the page can reload them. */
  onImported?: () => void;
  /** Returns the entry button of the tab that is active at the moment it is called, or
   *  `null`. `closeImport` hands focus to it, so focus never drops to `<body>` when the
   *  button that opened the panel has since unmounted. Never a tab trigger: Radix
   *  selects a tab whose trigger receives focus. */
  activeEntryButton?: () => HTMLElement | null;
}

export interface UseCsvImportResult {
  /** Which entry point opened the panel, or `null` when it is closed. Fixes the
   *  parse-request type — and so the path every call goes to — for the whole
   *  pipeline; there is no in-panel choice any more. */
  activeImport: ParseRequestType | null;
  /** `trigger` is the button the caller clicked to open the panel — pass
   *  `event.currentTarget`. Stored so `closeImport` can hand focus back to it; both
   *  entry buttons live in different components (`OperationsTab.tsx`, `detail.tsx`), so
   *  the hook is the only place able to hold it across the panel's lifetime. */
  openImport: (requestType: ParseRequestType, trigger?: HTMLElement | null) => void;
  /** Closes the panel and resets the pipeline, same as a fresh "Start over". Returns
   *  focus to the entry button that opened the panel while it is still in the document —
   *  see `openImport` — and otherwise to the active tab's entry button
   *  (`activeEntryButton`). */
  closeImport: () => void;
  /** Re-types an open panel to `requestType` when the user moves to the other tab before
   *  the upload started: the inputs reset exactly as a "Start over" resets them, and the
   *  panel stays open. The upload counts as started while `submit` is running, or once an
   *  attachment or a parse request is on record. A submit that ended before an
   *  attachment existed (a refused upload, or the date check before it) does not count,
   *  so a tab switch after it still re-types and clears the error. A no-op once the
   *  upload started, while no panel is open, or for the type already open. Leaves focus
   *  where the tab switch put it. */
  retypeImport: (requestType: ParseRequestType) => void;

  file: File | null;
  fileRejection: CsvRejectionCode | null;
  chooseFile: (chosen: File | null) => void;

  /** The position date sent as `reference_time` — read and rendered only for
   *  `position-parse-request` imports; `trade-parse-request` never shows or sends it. */
  referenceDate: string;
  setReferenceDate: (value: string) => void;
  /** Whether the file is declared the complete statement of the portfolio at
   *  `referenceDate`: every position it does not list is closed as of that instant. Sent
   *  as `is_complete` and composed into the idempotency key, for a request type whose
   *  config declares it (`IMPORT_TYPE_CONFIG.declaresCompleteness`) — a blotter declares
   *  nothing, so the trade flow never reads or sends this. Always starts false: closing
   *  positions the user did not mean to close is the destructive half of this import, so
   *  it is never the default, never remembered from a previous import, and never carried
   *  across a request-type switch. */
  isComplete: boolean;
  setIsComplete: (value: boolean) => void;
  /** Keyed like `SNAPSHOT_FIELD_MAP` in `DeclareSnapshotDialog` — currently only ever
   *  carries `referenceTime`, from the pre-submit check in `submit` below. */
  fieldErrors: Record<string, string>;

  attachmentId: string | null;
  requestId: string | null;
  requestWasAdopted: boolean;
  dispatchRefused: boolean;
  isSubmitting: boolean;
  generalError: string | null;

  submit: () => void;
  restart: () => void;
  retryDispatch: () => void;
  /** Non-null only while `parseStatus === 'failed'` AND the request's `reasons` name a
   *  DIFFERENT `ParseRequestType` than `activeImport` — a request whose reasons
   *  merely confirm the request type already chosen is not a suggestion, so the panel
   *  renders the generic no-rows-mapped card for that case instead. `null` on an older
   *  deployment that sends no `reasons` at all, since `suggestedRequestTypeFromReasons`
   *  reads a missing key the same as an empty one. */
  suggestedImportType: ParseRequestType | null;
  /** Switches the pipeline to the request type named by `suggestedImportType`, reusing
   *  the attachment already uploaded and scanned rather than asking for the file
   *  again. Sets `awaitingTypeSwitch`, which returns the panel to its pre-dispatch
   *  form — see that flag's own comment for why this deliberately does NOT
   *  auto-dispatch. */
  switchImportType: (requestType: ParseRequestType) => void;
  /** True from the moment `switchImportType` runs until the next `submit` dispatches (or
   *  `resetPipeline` clears the whole pipeline). Gates the panel back to the pre-dispatch
   *  form even though `attachmentId` is still set — see `switchImportType`'s own comment
   *  for why a switch must not fire straight into a new dispatch on its own. */
  awaitingTypeSwitch: boolean;

  attachmentStatus?: AttachmentStatus;
  /** True on a hard fetch error — every read that came back has failed. Kept distinct
   *  from `scanTimedOut`: a timeout means every read SUCCEEDED and simply never turned
   *  terminal, which is not the same claim as a confirmed failure. */
  scanFailed: boolean;
  /** True once the scan poll has run past `STALL_THRESHOLD_MS` with no terminal status
   *  and no error — the pipeline may still complete, so this stays distinct from
   *  `scanFailed`. */
  scanStalled: boolean;
  /** True once the scan poll gave up after running past `POLL_TIMEOUT_MS` with no
   *  terminal status and no error. Distinct from `scanFailed`: nothing here is known to
   *  have failed, only that this page stopped checking — `ImportPanel` renders a
   *  non-destructive `role="status"` card for this, never the fetch-error card. */
  scanTimedOut: boolean;
  retryScan: () => void;

  parseStatus?: ParseRequestStatus;
  isTerminal: boolean;
  /** True on a hard fetch error — every read that came back has failed. Kept distinct
   *  from `parseTimedOut`: a timeout means every read SUCCEEDED and simply never turned
   *  terminal, which is not the same claim as a confirmed failure. */
  parseFailed: boolean;
  /** True once the parse poll has run past `STALL_THRESHOLD_MS` with no terminal status
   *  and no error — the pipeline may still complete, so this stays distinct from
   *  `parseFailed`. */
  parseStalled: boolean;
  /** True once the parse poll gave up after running past `POLL_TIMEOUT_MS` with no
   *  terminal status and no error. Distinct from `parseFailed`: nothing here is known to
   *  have failed, only that this page stopped checking — `ImportPanel` renders a
   *  non-destructive `role="status"` card for this, never the fetch-error card. */
  parseTimedOut: boolean;
  retryParse: () => void;

  /** The calendar day (`YYYY-MM-DD`) the settled request itself was dispatched with, for
   *  the "applied" line below — read off `requestQuery.data.reference_time`, never off
   *  the live `referenceDate` field, so an adopted request still names the date it
   *  actually settled with even once the form has moved on to a later attempt. `null`
   *  before the request settles, on a `trade-parse-request` (which carries no
   *  `reference_time` at all), or on a deployment that sends none. */
  settledReferenceDate: string | null;
  /** True only when the request settled `success` AND the parser reports the declaration
   *  was actually made. The parser flips `is_complete` to false on every terminal event
   *  that declared nothing, so anything else — a `partial`, a deployment that sends no
   *  flag at all — reads false here: the panel must never claim a declaration it cannot
   *  see confirmed. */
  completenessApplied: boolean;
  /** The parser's own sentence explaining that the declaration was NOT applied, or null
   *  when the request carries no such reason. Rendered as it comes — see
   *  `completenessNotAppliedMessage` in `api/parse-request.ts`. */
  completenessNotAppliedMessage: string | null;

  summarySuccessCount: number | null;
  summaryRejectedCount: number | null;
  result: ParseResult | null;
  rowsNeeded: boolean;
  rowsLoading: boolean;
  rowsFailed: boolean;
  retryRows: () => void;
}

/**
 * The whole CSV import pipeline (upload -> malware scan -> parse -> results), lifted out
 * of the rendering tab so it survives a tab switch. Call this once, above the `Tabs`, so
 * its state — and the polls its queries run — live for the page's whole lifetime rather
 * than the launching tab's. Before the upload starts, a tab switch re-types the open
 * panel instead (`retypeImport`). `ImportPanel` only renders what this hook hands it; it
 * holds no pipeline state of its own.
 */
export function useCsvImport({
  portfolioId,
  sessionId,
  accountScope,
  onImported,
  activeEntryButton,
}: UseCsvImportOptions): UseCsvImportResult {
  const { t } = useLanguage();
  const [activeImport, setActiveImport] = useState<ParseRequestType | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileRejection, setFileRejection] = useState<CsvRejectionCode | null>(null);
  const [referenceDate, setReferenceDateState] = useState<string>(today);
  const [isComplete, setIsComplete] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  // True when the dispatch adopted a parse request an earlier attempt had recorded —
  // the results then describe that earlier import, and the results view says so.
  const [requestWasAdopted, setRequestWasAdopted] = useState(false);
  // True once the dispatch has refused to adopt a recorded request booked into a
  // different portfolio. That refusal is deterministic — the same attachment and
  // request type always resolve to the same recorded request — so neither the Retry
  // button nor the "starting" progress card may keep offering a next step that cannot
  // exist; only "Start over" does, and it clears this.
  const [dispatchRefused, setDispatchRefused] = useState(false);
  // True from `switchImportType` until the next `submit` dispatches. `inProgress` in
  // `ImportPanel` reads this alongside `attachmentId`/`requestId` so a switch returns
  // the panel to the pre-dispatch form under the NEW request type — reusing the
  // already-uploaded file — instead of straight into a progress card for a dispatch
  // that has not been asked for yet. See `switchImportType`'s own comment for why it
  // must not auto-dispatch.
  const [awaitingTypeSwitch, setAwaitingTypeSwitch] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  // Bumped by the retry button so the dispatch effect below runs again for the same
  // attachment. The attachment id cannot carry that signal — it has not changed, and
  // changing it would restart the scan poll for a file that is already cleared.
  const [dispatchNonce, setDispatchNonce] = useState(0);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // `t` is a fresh closure every time `LanguageProvider` re-renders — listing it in an
  // effect's dependency array re-fires that effect on a language switch alone. Read
  // through a ref instead, the same way `onImportedRef` below protects `onImported`.
  const tRef = useRef(t);
  tRef.current = t;

  /**
   * The tab that launched an import is not a guarantee the user stays there — but the
   * portfolio route param is the one thing that must never move under an in-flight
   * pipeline. Declared here, above the dispatch effect below, so that effect's own
   * closure can read it synchronously: React runs effects in declaration order, and the
   * dispatch effect is declared first, so on a `portfolioId` change it would otherwise
   * run with the *new* `portfolioId` already in scope, one full effect pass before the
   * reset effect (further down) gets a chance to close the panel. Comparing against this
   * ref inside the dispatch effect's own body catches that change synchronously, in the
   * same pass, rather than one render late.
   */
  const portfolioIdRef = useRef(portfolioId);

  const scanQuery = useAttachmentScanQuery(sessionId, accountScope, attachmentId);
  const requestQuery = useParseRequestQuery(sessionId, accountScope, activeImport, requestId);
  const parseStatus = requestQuery.data?.status;
  const isTerminal = isTerminalParseStatus(parseStatus);
  // True while `submit` is running, or once an attachment or a parse request is on record.
  // From then on a tab switch keeps the request type. A refused submit that left no
  // attachment is not a start: the panel holds only inputs again.
  const uploadStarted = isSubmitting || attachmentId != null || requestId != null;

  // A request whose reasons name the request type ALREADY chosen is not a suggestion —
  // the user just tried that request type and it still mapped nothing, so the generic
  // no-rows-mapped card is the honest one. Gating on `parseStatus === 'failed'` (rather
  // than trusting `suggestedRequestTypeFromReasons` alone) keeps this `null` for every
  // other status — a non-`failed` request's `reasons`, if it ever carries one, names
  // something else entirely and must not be read as this suggestion.
  const namedShapeType = suggestedRequestTypeFromReasons(requestQuery.data?.reasons);
  const suggestedImportType = parseStatus === 'failed' && namedShapeType !== activeImport
    ? namedShapeType
    : null;

  // Gated the same way `suggestedImportType` above gates its own reasons read: a
  // `created`/`processing` request has not settled yet, and a `failed` request never
  // reached dispatch, so its `reasons` name why the file never parsed, never a
  // declaration outcome — reading `completeness-not-applied` off it would be reading the
  // right field for the wrong meaning. The one render site (`ImportResults`) is itself
  // gated on a terminal, non-`failed` status today, so this is harmless there; gating
  // here too means the invariant survives a future call site that isn't.
  const settledCompletenessNotAppliedMessage =
    parseStatus === 'success' || parseStatus === 'partial' || parseStatus === 'rejected'
      ? completenessNotAppliedMessage(requestQuery.data?.reasons)
      : null;

  // `requestQuery.markAdopted` is a fresh closure every render, same as `t` above —
  // listing `requestQuery` itself in the dispatch effect's dependency array below would
  // re-fire that effect (and re-dispatch) on every render this hook is not the trigger
  // for. Read through a ref instead, the same way `tRef` protects `t`.
  const requestMarkAdoptedRef = useRef(requestQuery.markAdopted);
  requestMarkAdoptedRef.current = requestQuery.markAdopted;

  // The poll's `include_fields=results` summaries are the headline counts, delivered
  // free with every read. The full `/result` payload carries every raw row, so it is
  // fetched only when there are rejected rows to list — or, if a deployment embedded
  // no summaries at all, as the fallback the counts must then be derived from.
  const resultSummaries = requestQuery.data?.results;
  const successSummary = resultSummaries?.find((summary) => summary.outcome === 'success');
  const rejectedSummary = resultSummaries?.find((summary) => summary.outcome === 'rejected');
  const countsKnown = successSummary != null || rejectedSummary != null;
  const summarySuccessCount = countsKnown ? (successSummary?.row_count ?? 0) : null;
  const summaryRejectedCount = countsKnown ? (rejectedSummary?.row_count ?? 0) : null;
  const rowsNeeded = isTerminal && parseStatus !== 'failed'
    && (countsKnown ? (summaryRejectedCount ?? 0) > 0 : true);
  const resultQuery = useParseResultQuery(
    sessionId, accountScope, activeImport, requestId, rowsNeeded,
  );

  const attachmentStatus = scanQuery.data?.status;

  /**
   * A scan that clears hands straight over to the parse request, so the user is not
   * asked to press a second button for a step that has no decision in it.
   *
   * The ref is set *before* the await and keyed on the attachment, so the dispatch
   * happens exactly once per upload — React 18's development double-invoke of effects
   * would otherwise create two parse requests for one file. The deterministic
   * `unique_id` is the server-side backstop for the same race: a repeat is refused
   * `already-exists`, and the refusal is recovered by adopting the recorded request.
   *
   * After each await the ref is compared against the attachment this closure
   * dispatched for: "Start over" (and closing the panel) clears it and a new upload
   * replaces it, so a response that resolves after either must change nothing — the
   * user has already left this import behind.
   */
  const dispatchedAttachmentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeImport) return;
    // Synchronous with the render that changed `portfolioId` — this runs before the
    // reset effect further down gets a chance to close the panel (see `portfolioIdRef`
    // above). Without this, a dispatch left eligible to retry (the two states that null
    // `dispatchedAttachmentRef`, below) would fire once more with the *new* portfolio.
    if (portfolioIdRef.current !== portfolioId) return;
    if (attachmentStatus !== 'success' || !attachmentId) return;
    if (dispatchedAttachmentRef.current === attachmentId) return;
    // `switchImportType` resets `dispatchedAttachmentRef` on an attachment this effect
    // would otherwise see as immediately dispatchable (already `success`, never
    // dispatched under the new request type) — this guard is what keeps the switch
    // from firing straight into a fresh dispatch on its own. `submit` clears the flag
    // when the user actually asks for the new dispatch, at which point this effect
    // re-evaluates and proceeds normally.
    if (awaitingTypeSwitch) return;
    dispatchedAttachmentRef.current = attachmentId;

    const dispatchType = activeImport;
    // Read once here, at dispatch time, for the same reason `dispatchReferenceTime` below
    // is: the confirmation dialog that set it is already gone by now. `declaresCompleteness`
    // is what decides both whether the field is sent and whether the key can carry the
    // `/complete` segment, so a request type that declares nothing dispatches exactly the
    // bytes it did before — whatever the checkbox last held under the other request type.
    const declaresCompleteness = IMPORT_TYPE_CONFIG[dispatchType].declaresCompleteness;
    const dispatchIsComplete = declaresCompleteness && isComplete;
    const uniqueId = parseRequestUniqueId(attachmentId, dispatchType, dispatchIsComplete);
    // `reference_time` dates every row in a holdings file — which carries no date column
    // of its own — and the completeness declaration alike; a blotter row always carries
    // its own date, and `/trade-parse-request` refuses the key outright — so the request
    // carries it only for a request type whose config says so (`IMPORT_TYPE_CONFIG`).
    // Read once here, at dispatch time: by now the form field that set it is already
    // gone (the panel switched to the progress card the moment the upload finished), so
    // nothing can change it out from under an in-flight dispatch. The request type
    // itself is not in the body: it is the path the batch goes to.
    const dispatchReferenceTime = IMPORT_TYPE_CONFIG[dispatchType].requiresReferenceDate
      ? composeReferenceTimeUtcMidnight(referenceDate)
      : undefined;
    const startParsing = async () => {
      try {
        const requests = await createParseRequests(sessionId!, accountScope!, dispatchType, [
          {
            attachment_id: attachmentId,
            portfolio_id: portfolioId!,
            unique_id: uniqueId,
            ...(dispatchReferenceTime ? { reference_time: dispatchReferenceTime } : {}),
            // Mandatory on the declaring route, so `false` is sent as itself rather than
            // omitted — the parser refuses a holdings request that carries no flag at all.
            ...(declaresCompleteness ? { is_complete: dispatchIsComplete } : {}),
          },
        ]);
        if (!isMounted.current || dispatchedAttachmentRef.current !== attachmentId) return;
        if (!requests[0]) {
          setGeneralError(tRef.current('portfolio.import.startFailed'));
          return;
        }
        setRequestId(requests[0].id);
      } catch (err) {
        if (!isMounted.current || dispatchedAttachmentRef.current !== attachmentId) return;
        if (err instanceof BackendValidationError && err.code === 'already-exists') {
          // `undefined` means the verification read itself failed — unknown, which must
          // not collapse into "none recorded": the request likely exists server-side,
          // so the message says confirming failed and the retry re-runs this adoption.
          const existing = await findParseRequestByUniqueId(
            sessionId!, accountScope!, dispatchType, uniqueId,
          ).catch(() => undefined);
          if (!isMounted.current || dispatchedAttachmentRef.current !== attachmentId) return;
          if (existing) {
            // The parser's key carries the attachment and operation but not the
            // portfolio, so the recorded request may have booked its rows into a
            // different one.
            // Adopting it would present another portfolio's results as this import's.
            // The refusal is deterministic for this attachment — a Retry would only
            // repeat it — so `dispatchedAttachmentRef` stays set and `dispatchRefused`
            // suppresses both the Retry button and the "starting" progress card below.
            if (existing.portfolio_id !== portfolioId) {
              setDispatchRefused(true);
              setGeneralError(tRef.current('portfolio.import.importedElsewhere'));
              return;
            }
            setRequestWasAdopted(true);
            // Rearms the elapsed-time clock BEFORE the id itself changes to the adopted
            // (possibly already-old) request's id — see `markAdopted` in
            // `use-parse-import-queries.ts` for why this has to happen in that order.
            // Adoption is the user's own deliberate re-upload, the same act as a manual
            // Retry, and grants the same fresh ten-minute window rather than landing
            // straight back on the timed-out card with zero polls run. Read through
            // `requestMarkAdoptedRef` (see that ref's own comment) rather than closing
            // over `requestQuery.markAdopted` directly, so this effect's dependency
            // array does not need `requestQuery` itself.
            requestMarkAdoptedRef.current();
            setRequestId(existing.id);
            return;
          }
          if (existing === undefined) {
            dispatchedAttachmentRef.current = null;
            setGeneralError(tRef.current('portfolio.import.startUnconfirmed'));
            return;
          }
        }
        // Let this attachment be dispatched again by the retry button below: the
        // failure may have been transient, and the file is already stored and scanned.
        dispatchedAttachmentRef.current = null;
        const { fieldErrors: backendFieldErrors, generalError: backendError } =
          parseBackendErrors(err, IMPORT_FIELD_MAP);
        // The form that carried the `referenceTime` field is already gone by the time
        // this dispatch runs (see `dispatchReferenceTime` above), so a rejection of it
        // has no field to land on either — it reads through the general banner, same as
        // `file`'s own backend rejections do in `submit` below. Combined rather than
        // picked, so a response naming both `reference_time` and something else does not
        // silently drop the second message.
        setGeneralError(
          isUnreadableResponseError(err)
            ? tRef.current('errors.unreadableResponse')
            : combineWithGeneralError(backendFieldErrors.referenceTime, backendError)
              || tRef.current('portfolio.import.startFailed'),
        );
      }
    };
    void startParsing();
  }, [
    activeImport, attachmentStatus, attachmentId, sessionId, accountScope, portfolioId,
    dispatchNonce, referenceDate, isComplete, awaitingTypeSwitch,
  ]);

  /**
   * A settle that booked rows means the positions, the NAV and the blotter this page
   * shows are now stale — `failed` and `rejected` booked nothing, so they trigger no
   * refetch. Fired once per request, read through a ref so a re-render of the parent's
   * callback cannot re-fire it.
   */
  const onImportedRef = useRef(onImported);
  onImportedRef.current = onImported;
  const reportedRequestRef = useRef<string | null>(null);
  useEffect(() => {
    if (!requestId || !isTerminal) return;
    if (reportedRequestRef.current === requestId) return;
    reportedRequestRef.current = requestId;
    if (parseStatus === 'success' || parseStatus === 'partial') {
      onImportedRef.current?.();
    }
  }, [requestId, isTerminal, parseStatus]);

  /**
   * Bails an upload continuation the user has left behind — the same shape
   * `dispatchedAttachmentRef` gives the dispatch path. Set to the uploading file
   * before the awaits and compared after each one; the picker and the drop zone are
   * disabled while an upload is in flight, so nothing on screen can swap the file
   * mid-upload today — the guard keeps that true for any control added later.
   */
  const uploadingFileRef = useRef<File | null>(null);

  /**
   * Which `File` object the current `attachmentId` was actually uploaded (or adopted)
   * from. `switchImportType` deliberately keeps `attachmentId` across a switch so an
   * unchanged file can be reused without a second upload — but that reuse is only valid
   * while `file` is still the exact object this ref names. `chooseFile` below compares
   * against it to invalidate `attachmentId` the instant the user picks a different file
   * (including clearing the current one), and `submit` compares against it to decide
   * whether a click on Start can skip straight to dispatch or must upload first. Without
   * this, a switch followed by picking a corrected file left the stale attachment armed:
   * `submit` clears `awaitingTypeSwitch` in its synchronous prelude, before its own
   * `await readCsvText(file)` even starts, and the dispatch effect (gated only by
   * `awaitingTypeSwitch` and `dispatchedAttachmentRef`, both already clear from the
   * switch) fired for the OLD attachment under the NEW type before the new upload had
   * even begun — booking rows from a file the user had visibly withdrawn.
   */
  const attachmentFileRef = useRef<File | null>(null);

  /**
   * Escapes an upload key the SharedCache still blocks even though nothing usable is
   * on record for it (see the `existing === null` branch in `submit` below). Reset
   * whenever a file is chosen or the pipeline restarts; bumped only by that branch, so
   * the very next submit for the same file reaches the store instead of replaying the
   * same refusal for up to 30 days.
   */
  const uploadKeySaltRef = useRef(0);

  // Unlike the old tab's "Start over", the header close button (below) is reachable
  // while an upload is still running — the header sits outside the `isSubmitting`-gated
  // form, so nothing before this design ever needed the reset to touch `isSubmitting`
  // itself. `uploadingFileRef` already bails the abandoned upload's own continuation,
  // but that only prevents it from setting state *later*; without clearing the flag
  // here too, a close-then-reopen would show a fresh panel stuck on "Uploading…" for a
  // request nothing is actually running any more.
  const resetPipeline = () => {
    dispatchedAttachmentRef.current = null;
    uploadingFileRef.current = null;
    attachmentFileRef.current = null;
    uploadKeySaltRef.current = 0;
    setFile(null);
    setFileRejection(null);
    // Refreshed here too, alongside `openImport` below — a `restart()` mid-session (the
    // "Start over" button) begins a new upload without going through `openImport` again,
    // so this is the only recompute a same-session restart gets.
    setReferenceDateState(today());
    setIsComplete(false);
    setFieldErrors({});
    setAttachmentId(null);
    setRequestId(null);
    setRequestWasAdopted(false);
    setDispatchRefused(false);
    setAwaitingTypeSwitch(false);
    setGeneralError(null);
    setIsSubmitting(false);
  };

  // The button that opened the panel, so closing it can hand focus back rather than
  // dropping it to `<body>` — the browser's default when the element that held focus
  // (the panel's own close button) is removed from the document. Not component state:
  // it never needs to trigger a render, and living in a ref means a change to it can
  // never itself cause the panel to re-render.
  const openTriggerRef = useRef<HTMLElement | null>(null);

  /**
   * The tab that launched an import is not a guarantee the user stays there — but the
   * portfolio route param is the one thing that must never move under an in-flight
   * pipeline. A `key={portfolioId}` on the old, single-tab component used to remount the
   * whole subtree and throw every ref and query away on a portfolio change. That guard
   * moved with the state: this effect is what replaces it now that the state lives above
   * the tabs and no longer remounts with the route. Without it, a parsed row from the
   * portfolio the import was started on would book into whatever portfolio the URL now
   * names — a silent money-corruption bug, not a UI glitch. Closing the panel outright,
   * not merely clearing the file, is deliberate: a panel left open for a portfolio that
   * no longer matches its own state has nothing true left to show.
   *
   * `portfolioIdRef` itself is declared above, alongside `tRef` — the dispatch effect
   * reads it synchronously so a portfolio change is caught before it dispatches, not
   * only after this effect gets around to resetting the pipeline.
   */
  useEffect(() => {
    if (portfolioIdRef.current === portfolioId) return;
    portfolioIdRef.current = portfolioId;
    resetPipeline();
    setActiveImport(null);
    // Not a `closeImport()` call: nothing here clicked the close button, so nothing
    // should steal focus back to the entry button — the navigation that changed
    // `portfolioId` already moved focus somewhere the user chose. Only drop the stale
    // reference so a later real close never restores focus to the wrong portfolio's
    // trigger.
    openTriggerRef.current = null;
  }, [portfolioId]);

  const openImport = (requestType: ParseRequestType, trigger?: HTMLElement | null) => {
    openTriggerRef.current = trigger ?? null;
    // Refreshed on every fresh open, not only on reset: `referenceDate` otherwise keeps
    // whatever "today" was current the last time the pipeline was reset (page mount, or
    // the previous panel's close) — a dashboard left open overnight then prefills
    // yesterday's date for a file being imported this morning. Both entry buttons that
    // call this are `aria-disabled` while a panel is already open (`PortfolioDetail`,
    // `OperationsTab`), so this only ever runs on a closed-to-open transition and never
    // clobbers a date the user is mid-edit on.
    setReferenceDateState(today());
    // A declaration is never inherited from the last import the panel ran — see
    // `isComplete` in this hook's result type. The confirmation dialog clears it again on
    // every open, so this is belt and braces for the one path that skips the dialog
    // entirely: a request type that declares nothing.
    setIsComplete(false);
    setActiveImport(requestType);
  };

  const closeImport = () => {
    resetPipeline();
    setActiveImport(null);
    const trigger = openTriggerRef.current;
    openTriggerRef.current = null;
    // The trigger may have unmounted while the panel was open (a tab switch after the
    // upload started, a re-type, the tab an "Import as ..." continuation selected). Its
    // tab then is not the active one, and the active tab's entry button takes its place.
    // Both entry buttons use `aria-disabled` rather than the native attribute precisely
    // so they stay focusable even while blocked (see OperationsTab.tsx / detail.tsx), so
    // no separate disabled check is needed here.
    const focusTarget = trigger && document.contains(trigger) ? trigger : activeEntryButton?.();
    focusTarget?.focus();
  };

  // The panel follows the active tab only while it holds nothing but inputs. Once the
  // upload started there is a file on record under this request type, and the panel
  // keeps it on both tabs. A `switchImportType` keeps its reused attachment, so it counts
  // as started too, until `chooseFile` picks another file and clears it. The stored
  // trigger is dropped: the entry button it names sat in the tab that just unmounted, so
  // a Close after a re-type focuses the active tab's entry button (`activeEntryButton`).
  const retypeImport = (requestType: ParseRequestType) => {
    if (activeImport == null || activeImport === requestType || uploadStarted) return;
    resetPipeline();
    openTriggerRef.current = null;
    setActiveImport(requestType);
  };

  const restart = () => {
    resetPipeline();
  };

  const retryDispatch = () => {
    setGeneralError(null);
    dispatchedAttachmentRef.current = null;
    setDispatchNonce((nonce) => nonce + 1);
  };

  /**
   * Continues under the OTHER request type a `failed` request's `reasons` named — see
   * `suggestedImportType` above. `attachmentId` and `file` are deliberately kept: the
   * attachment is already uploaded and virus-scanned, and the parser namespaces its
   * idempotency key by request type (`parseRequestUniqueId` composes into the request
   * type's own operation), so a request over the same attachment under the other
   * request type is a legal, distinct dispatch, never a duplicate of the one that just
   * failed. Everything scoped to the FAILED request resets, same as `resetPipeline`
   * would clear it, since it describes a request this pipeline no longer has.
   *
   * This does NOT dispatch on its own — `awaitingTypeSwitch` instead returns the panel
   * to its pre-dispatch form under the new request type. `position-parse-request`
   * sends a request-level `reference_time` the trade flow never asks for;
   * auto-dispatching here would stamp today's date on a holdings file behind the
   * user's back instead of asking for the position date the new request type actually
   * needs.
   */
  const switchImportType = (requestType: ParseRequestType) => {
    dispatchedAttachmentRef.current = null;
    setRequestId(null);
    setRequestWasAdopted(false);
    setDispatchRefused(false);
    setGeneralError(null);
    setFieldErrors({});
    // Same reasoning as `openImport` above: a switch that kept a stale date would
    // silently resubmit whatever "today" was when the panel first opened, possibly a
    // date old by however long the failed attempt and its investigation took.
    setReferenceDateState(today());
    // Same reasoning as `openImport` above: the new request type asks for its own
    // confirmation, and a declaration ticked for the request type that just failed must
    // never carry into it.
    setIsComplete(false);
    setActiveImport(requestType);
    setAwaitingTypeSwitch(true);
  };

  const chooseFile = (chosen: File | null) => {
    setGeneralError(null);
    uploadKeySaltRef.current = 0;
    // A switch keeps `attachmentId` set for reuse (see `switchImportType`), but that
    // attachment describes the file it was actually uploaded from — `attachmentFileRef`.
    // The moment the user picks something else here, including clearing the field
    // outright, that attachment no longer describes what the form is about to submit.
    // Null it out synchronously, in the same tick as the pick, so the dispatch effect's
    // `!attachmentId` guard blocks it immediately — before `submit` ever runs, and
    // regardless of the effect's other gates. See `attachmentFileRef`'s own comment for
    // the failure this closes.
    if (attachmentId != null && chosen !== attachmentFileRef.current) {
      setAttachmentId(null);
      attachmentFileRef.current = null;
    }
    if (!chosen) {
      setFile(null);
      setFileRejection(null);
      return;
    }
    const rejection = csvRejection(chosen);
    setFileRejection(rejection);
    setFile(rejection ? null : chosen);
  };

  const setReferenceDate = (value: string) => {
    setReferenceDateState(value);
    setFieldErrors((prev) => {
      if (!prev.referenceTime) return prev;
      const rest = { ...prev };
      delete rest.referenceTime;
      return rest;
    });
  };

  const submit = async () => {
    if (!file || isSubmitting || !sessionId || !accountScope) return;
    // A cleared or hand-edited invalid date is a field error, never a request — the
    // check runs only for a request type whose config says it sends this field
    // (`IMPORT_TYPE_CONFIG`), since `trade-parse-request` never renders or sends it. Bail
    // before the upload starts, same as any other pre-submit check: the file would
    // upload for nothing if the request behind it can never be dispatched.
    if (activeImport != null && IMPORT_TYPE_CONFIG[activeImport].requiresReferenceDate
      && !isValidCalendarDate(referenceDate)) {
      setFieldErrors({ referenceTime: t('portfolio.import.referenceTimeInvalid') });
      return;
    }
    // `attachmentId` set for exactly the file still in the form means a switch kept a
    // reuse candidate (see `switchImportType`) and the user never replaced it — the
    // attachment is already uploaded and virus-scanned, so there is nothing left to
    // await here. Skip straight to arming the dispatch: the effect fires for it once
    // `awaitingTypeSwitch` clears below, same as any other confirmed dispatch. Running
    // the upload code below anyway would be a redundant POST, and its failure branches
    // (`already-exists` adoption, the digest guard, `CsvFileError` nulling `file`) all
    // assume no attachment is on record yet — wrongly painting over a reuse that needs
    // none of them.
    if (attachmentId != null && attachmentFileRef.current === file) {
      setAwaitingTypeSwitch(false);
      setGeneralError(null);
      return;
    }
    uploadingFileRef.current = file;
    setIsSubmitting(true);
    // This IS the dispatch `awaitingTypeSwitch` gates — see that flag's own comment.
    // Cleared unconditionally, whether or not a switch is what led here, so it never
    // outlives the pre-dispatch step it exists to hold the panel on.
    setAwaitingTypeSwitch(false);
    setGeneralError(null);
    // Derived from the content, so it survives losing this component's state: assigned
    // before the upload and read by the recovery branch in the catch below.
    let uploadUniqueId: string | null = null;
    try {
      const content = await readCsvText(file);
      uploadUniqueId = await uploadUniqueIdFromContent(content, uploadKeySaltRef.current);
      const attachment = await uploadAttachment(sessionId, accountScope, {
        fileName: file.name,
        content,
        uniqueId: uploadUniqueId,
      });
      if (!isMounted.current || uploadingFileRef.current !== file) return;
      attachmentFileRef.current = file;
      setAttachmentId(attachment.id);
    } catch (err) {
      if (!isMounted.current || uploadingFileRef.current !== file) return;
      if (err instanceof CsvFileError) {
        setFileRejection(err.rejection);
        setFile(null);
        return;
      }
      // `already-exists` on the content-derived key means this same content was stored
      // by an earlier attempt — a retried timeout, or a re-selection after "Start
      // over" — and the recorded attachment is adopted instead of dead-ending on the
      // refusal. `undefined` means the verification read itself failed: unknown, not
      // "none recorded", so the message says confirming failed and submitting again
      // re-runs this same recovery with the same key. `ADOPTABLE_ATTACHMENT_STATUSES`
      // keeps a `failed` record out of `existing` entirely: adopting it would present a
      // dead end as recorded work, and the digest guard's own documented recovery for
      // `failed` is a fresh store, not a replay of this one.
      if (err instanceof BackendValidationError && err.code === 'already-exists' && uploadUniqueId) {
        const existing = await findAttachmentByUniqueId(
          sessionId, accountScope, uploadUniqueId, ADOPTABLE_ATTACHMENT_STATUSES,
        ).catch(() => undefined);
        if (!isMounted.current || uploadingFileRef.current !== file) return;
        if (existing) {
          // Rearms the elapsed-time clock BEFORE the id itself changes to the adopted
          // (possibly already-old) attachment's id — see `markAdopted` in
          // `use-parse-import-queries.ts` for why this has to happen in that order.
          // Adoption is the user's own deliberate re-upload, the same act as a manual
          // Retry, and grants the same fresh ten-minute window rather than landing
          // straight back on the timed-out card with zero polls run.
          scanQuery.markAdopted();
          attachmentFileRef.current = file;
          setAttachmentId(existing.id);
          return;
        }
        if (existing === undefined) {
          setGeneralError(t('portfolio.import.uploadUnconfirmed'));
          return;
        }
        // `null`: the key is cache-blocked, but nothing adoptable is on record for it —
        // either the earlier attempt ended `failed`, or the record is gone entirely.
        // Both dead-end the same way against the same key, so the next submit for this
        // file must use a fresh one instead of replaying this refusal for up to 30 days.
        uploadKeySaltRef.current += 1;
        setGeneralError(t('portfolio.import.uploadNotAdoptable'));
        return;
      }
      // The digest guard: the same bytes were stored earlier under a key this page
      // cannot derive (an upload from before keys were content-derived, or another
      // pipeline's), and `GET /attachment` has no digest lookup to adopt it with. The
      // backend's raw sentence names none of that, so a translated message with the
      // way out replaces it.
      if (err instanceof BackendValidationError && err.code === 'duplicate-file') {
        setGeneralError(t('portfolio.import.duplicateFile'));
        return;
      }
      const { fieldErrors: backendFieldErrors, generalError: backendError } =
        parseBackendErrors(err, IMPORT_FIELD_MAP);
      // A `file`-scoped backend rejection is still about the chosen file, but the
      // picker's slot renders only the coded client-side rejections, so the backend's
      // own message lands in the banner, field message first — combined rather than
      // picked, so a response naming both `file` and something else does not silently
      // drop the second message (see `combineWithGeneralError`).
      setGeneralError(
        isUnreadableResponseError(err)
          ? t('errors.unreadableResponse')
          : combineWithGeneralError(backendFieldErrors.file, backendError)
            || t('portfolio.import.uploadFailed'),
      );
    } finally {
      // Same pair guard as every continuation point above: this call may not be the
      // latest one any more (the panel was closed and reopened with a different file
      // mid-upload, its own submit already latched the ref onto the new file). Clearing
      // the latch unconditionally here would re-enable the form for a request nothing
      // is running, admitting a second concurrent submit of whatever file is now chosen.
      if (isMounted.current && uploadingFileRef.current === file) setIsSubmitting(false);
    }
  };

  return {
    activeImport,
    openImport,
    closeImport,
    retypeImport,

    file,
    fileRejection,
    chooseFile,

    referenceDate,
    setReferenceDate,
    isComplete,
    setIsComplete,
    fieldErrors,

    attachmentId,
    requestId,
    requestWasAdopted,
    dispatchRefused,
    isSubmitting,
    generalError,

    submit,
    restart,
    retryDispatch,
    suggestedImportType,
    switchImportType,
    awaitingTypeSwitch,

    attachmentStatus,
    scanFailed: scanQuery.isError,
    scanStalled: scanQuery.stalled,
    scanTimedOut: scanQuery.timedOut,
    retryScan: () => {
      scanQuery.resetElapsed();
      scanQuery.refetch();
    },

    parseStatus,
    isTerminal,
    parseFailed: requestQuery.isError,
    parseStalled: requestQuery.stalled,
    parseTimedOut: requestQuery.timedOut,
    retryParse: () => {
      requestQuery.resetElapsed();
      requestQuery.refetch();
    },

    // The wire value is the explicit UTC instant `composeReferenceTimeUtcMidnight`
    // composes (`YYYY-MM-DDT00:00:00+00:00`) — its first 10 characters are the calendar
    // day, and slicing is the whole conversion: reading it through a timezone (the way
    // `calendarDayInFilterZone` reads other instants) would risk moving the date itself,
    // which this value must never do. `formatDateDisplay` at the render site already
    // renders "N/A" for `null`, so no request-settled or wrong-request-type case needs
    // handling here beyond `?? null`.
    settledReferenceDate: requestQuery.data?.reference_time?.slice(0, 10) ?? null,
    completenessApplied: parseStatus === 'success' && requestQuery.data?.is_complete === true,
    completenessNotAppliedMessage: settledCompletenessNotAppliedMessage,

    summarySuccessCount,
    summaryRejectedCount,
    result: resultQuery.data ?? null,
    rowsNeeded,
    rowsLoading: resultQuery.isLoading,
    rowsFailed: resultQuery.isError,
    retryRows: () => resultQuery.refetch(),
  };
}
